import { apiClient } from './client';

export interface AffiliateApplication {
  _id: string;
  userId: string;
  userEmail?: string;
  status: 'pending' | 'approved' | 'rejected';
  fullName: string;
  country: string;
  companyName?: string;
  website?: string;
  socialMedia?: string;
  howWillPromote?: string;
  payoutMethod: 'bank' | 'paypal';
  iban?: string;
  paypalEmail?: string;
  taxId?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Affiliate {
  _id: string;
  code: string;
  ownerId: string;
  ownerEmail?: string;
  ownerName?: string;
  conversionsCount: number;
  totalEarnings: number;
  paidEarnings: number;
  pendingEarnings: number;
  payoutMethod?: 'bank' | 'paypal';
  payoutIban?: string;
  payoutPaypalEmail?: string;
  createdAt: string;
}

export type ReferralType = 'affiliate' | 'advertiser' | 'partner';

export interface Referral {
  _id: string;
  ownerId: {
    _id: string;
    email: string;
    username?: string;
  };
  code: string;
  type: ReferralType;
  commissionRate: number;
  userDiscountEnabled: boolean;
  stripeCouponMonthly?: string;
  stripeCouponYearly?: string;
  discountLabel?: string;
  notes?: string;
  conversions: {
    userId: string;
    subscriptionId: string;
    convertedAt: string;
    revenue: number;
    commissionEarned: number;
  }[];
  totalEarnings: number;
  pendingEarnings: number;
  paidEarnings: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReferralDto {
  ownerId?: string; // Optional - for advertisers/partners without user account
  code?: string; // Optional - auto-generated if not provided
  type?: ReferralType;
  commissionRate?: number;
  userDiscountEnabled?: boolean;
  stripeCouponMonthly?: string;
  stripeCouponYearly?: string;
  discountLabel?: string;
  notes?: string;
}

export interface UpdateReferralDto {
  type?: ReferralType;
  commissionRate?: number;
  userDiscountEnabled?: boolean;
  stripeCouponMonthly?: string;
  stripeCouponYearly?: string;
  discountLabel?: string;
  notes?: string;
  isActive?: boolean;
}

export interface AdminUser {
  _id: string;
  email: string;
  username?: string;
  subscriptionStatus?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid';
  referredBy?: string;
  isAffiliate: boolean;
  emailVerifiedAt?: string;
  createdAt: string;
  updatedAt: string;
  profile?: {
    companyName?: string;
    businessType?: string;
    venueName?: string;
  };
}

export interface UsersListResponse {
  users: AdminUser[];
  total: number;
  page: number;
  totalPages: number;
}

export interface Visit {
  _id: string;
  visitorId: string;
  referralCode?: string;
  landingUrl?: string;
  referrerUrl?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  locale?: string;
  userAgent?: string;
  screenWidth?: number;
  screenHeight?: number;
  convertedToUserId?: string;
  convertedAt?: string;
  createdAt: string;
}

export interface VisitsListResponse {
  visits: Visit[];
  total: number;
  page: number;
  totalPages: number;
}

export interface VisitStats {
  totalVisits: number;
  visitsWithReferral: number;
  conversions: number;
  conversionRate: number;
  todayVisits: number;
  todayConversions: number;
}

export interface ReferralFunnelItem {
  code: string;
  visits: number;
  uniqueVisitors: number;
  leads: number;
  conversions: number;
}

export const adminApi = {
  // Affiliate Applications
  async listApplications(status?: string): Promise<AffiliateApplication[]> {
    const params = status ? { status } : {};
    const response = await apiClient.get('/admin/affiliate-applications', { params });
    return response.data;
  },

  async approveApplication(applicationId: string): Promise<AffiliateApplication> {
    const response = await apiClient.post(`/admin/affiliate-applications/${applicationId}/approve`);
    return response.data;
  },

  async rejectApplication(applicationId: string, reason?: string): Promise<AffiliateApplication> {
    const response = await apiClient.post(`/admin/affiliate-applications/${applicationId}/reject`, { reason });
    return response.data;
  },

  // Affiliates
  async listAffiliates(): Promise<Affiliate[]> {
    const response = await apiClient.get('/admin/affiliates');
    return response.data;
  },

  async recordPayout(
    referralId: string,
    amount: number,
    method: 'bank' | 'paypal',
    reference?: string
  ): Promise<any> {
    const response = await apiClient.post(`/admin/affiliates/${referralId}/payout`, {
      amount,
      method,
      reference,
    });
    return response.data;
  },

  // Referrals (direct management)
  async listReferrals(filters?: { type?: string; isActive?: boolean }): Promise<Referral[]> {
    const params: Record<string, string> = {};
    if (filters?.type) params.type = filters.type;
    if (filters?.isActive !== undefined) params.isActive = String(filters.isActive);
    const response = await apiClient.get('/admin/referrals', { params });
    return response.data;
  },

  async createReferral(dto: CreateReferralDto): Promise<Referral> {
    const response = await apiClient.post('/admin/referrals', dto);
    return response.data;
  },

  async getReferral(id: string): Promise<Referral> {
    const response = await apiClient.get(`/admin/referrals/${id}`);
    return response.data;
  },

  async updateReferral(id: string, dto: UpdateReferralDto): Promise<Referral> {
    const response = await apiClient.patch(`/admin/referrals/${id}`, dto);
    return response.data;
  },

  // Visits (Analytics)
  async listVisits(options?: {
    page?: number;
    limit?: number;
    hasReferral?: boolean;
    converted?: boolean;
  }): Promise<VisitsListResponse> {
    const params: Record<string, string> = {};
    if (options?.page) params.page = String(options.page);
    if (options?.limit) params.limit = String(options.limit);
    if (options?.hasReferral !== undefined) params.hasReferral = String(options.hasReferral);
    if (options?.converted !== undefined) params.converted = String(options.converted);
    const response = await apiClient.get('/analytics/visits', { params });
    return response.data;
  },

  async getVisitStats(): Promise<VisitStats> {
    const response = await apiClient.get('/analytics/stats');
    return response.data;
  },

  async getReferralFunnel(): Promise<ReferralFunnelItem[]> {
    const response = await apiClient.get('/analytics/referral-funnel');
    return response.data;
  },

  // Users
  async listUsers(options?: {
    sortBy?: 'createdAt' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
    page?: number;
    limit?: number;
    hasReferral?: boolean;
  }): Promise<UsersListResponse> {
    const params: Record<string, string> = {};
    if (options?.sortBy) params.sortBy = options.sortBy;
    if (options?.sortOrder) params.sortOrder = options.sortOrder;
    if (options?.page) params.page = String(options.page);
    if (options?.limit) params.limit = String(options.limit);
    if (options?.hasReferral !== undefined) params.hasReferral = String(options.hasReferral);
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },
};
